import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

interface SelectableItem {
	id?: number;
	name?: string;
	Monitor_ID?: number;
	Monitor_Name?: string;
	[key: string]: any;
}

interface SearchableSelectProps<T extends SelectableItem> {
	label?: string;
	value: number | null;
	onChange: (id: number) => void;
	options: T[];
	placeholder?: string;
	error?: string;
	disabled?: boolean;
}

const getId = <T extends SelectableItem>(item: T): number => {
	return item.Monitor_ID ?? item.id ?? 0;
};

const getName = <T extends SelectableItem>(item: T): string => {
	return item.Monitor_Name ?? item.name ?? "";
};

const SearchableSelect = <T extends SelectableItem>({
	label,
	value,
	onChange,
	options,
	placeholder,
	error,
	disabled = false,
}: SearchableSelectProps<T>) => {
	const { t } = useTranslation();
	const [searchTerm, setSearchTerm] = useState("");
	const [isOpen, setIsOpen] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const selectedItem = options.find((o) => getId(o) === value) ?? null;
	const resolvedPlaceholder = placeholder ?? t("common.searchAndSelect");

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				wrapperRef.current &&
				!wrapperRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
				setSearchTerm("");
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	const filteredOptions = options.filter((option) =>
		getName(option).toLowerCase().includes(searchTerm.toLowerCase()),
	);

	const handleSelect = (item: T) => {
		onChange(getId(item));
		setIsOpen(false);
		setSearchTerm("");
	};

	const displayValue = isOpen ? searchTerm : selectedItem ? getName(selectedItem) : "";

	return (
		<div className="w-full" ref={wrapperRef}>
			{label && (
				<label className="block text-gray-700 text-sm font-semibold mb-2">
					{label}
				</label>
			)}

			<div className="relative">
				<input
					ref={inputRef}
					type="text"
					value={displayValue}
					onChange={(e) => {
						setSearchTerm(e.target.value);
						setIsOpen(true);
					}}
					onFocus={() => {
						setIsOpen(true);
						setSearchTerm("");
					}}
					placeholder={resolvedPlaceholder}
					disabled={disabled}
					className={`appearance-none border rounded-lg w-full py-2.5 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed transition-all duration-200 bg-white shadow-sm hover:shadow-md ${
						error ? "border-red-400 focus:ring-red-500" : "border-gray-300"
					}`}
				/>

				{isOpen && !disabled && (
					<div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-soft-lg max-h-60 overflow-auto">
						{filteredOptions.length === 0 ? (
							<div className="px-4 py-3 text-gray-500 text-sm">
								{searchTerm ? t("common.noResults") : t("common.noOptions")}
							</div>
						) : (
							<ul className="py-1">
								{filteredOptions.map((option) => (
									<li
										key={getId(option)}
										onClick={() => handleSelect(option)}
										className={`px-4 py-2.5 cursor-pointer transition-colors duration-150 ${
											getId(option) === value
												? "bg-gradient-to-r from-primary-100 to-primary-200 text-primary-800 font-medium"
												: "hover:bg-gradient-to-r hover:from-primary-50 hover:to-primary-100"
										}`}
									>
										{getName(option)}
									</li>
								))}
							</ul>
						)}
					</div>
				)}
			</div>

			{error && <p className="mt-1 text-sm text-red-600">{error}</p>}
		</div>
	);
};

export default SearchableSelect;
